import { IdGeneratorService } from './../../../../../services/id-generator.service';
import { CostsPrice, Price, RetailPrice, RetailPriceCalculationValue } from './../../../../erp-common/erp-common.model';
import { ArticleType, ArticleTypeCalculationPrecission, AttributeGroupUsage } from './../../../../article-types/article-type.model';
import { Article, BasicArticle, CostsPriceSources } from './../../../catalog.model';
import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl as FormControl, UntypedFormGroup as FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AppClientConfigurationService, AppServerAPI, CUSTOM_DATE_FORMAT } from '@sdbn/common';
import { DictionaryEntry } from '@sdbn/common';
import { DateTime } from 'luxon';

@Component({
    selector: 'app-price-editor',
    templateUrl: './price-editor.component.html',
    styleUrls: ['./price-editor.component.scss'],
    standalone: false
})
export class PriceEditorComponent implements OnInit {

  article: Article;

  articleType: ArticleType;

  price: Price;

  costsPriceSources = new Array<string>();

  formGroup = new FormGroup({});

  costsPriceAttributeGroups = new Array();

  confirmDisabled = false;

  hideVatPrices = true;
  priceScale = 2;

  mode = 'retail';

  basic = false;

  allowBasicPriceSelectionOption = true;

  useBasicPriceOptionSelected = false;

  calculationResults = new Map<number, number>();

  processingFormChange = false;

  basicArticle: BasicArticle = null;

  result = 'open';

  resultingPriceCalculationDate:Date;
  resultingPriceNet: number;

  constructor(
    public cps: AppClientConfigurationService,
    public dialogRef: MatDialogRef<PriceEditorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private idGenerator: IdGeneratorService,
    private appServerAPI: AppServerAPI
  ) {

    if (data.basic != null) {
      this.basic = data.basic;
    }

    if (data.article != null) {
      this.article = data.article;
    }

    if (data.articleType != null) {
      this.articleType = data.articleType;
    }

    if (data.price != null) {
      this.price = data.price;
    }

    if (this.price instanceof CostsPrice) {
      this.mode = 'costs';
    }
    this.hideVatPrices = this.cps.getPropertyValue('erp.hide-vat-prices', 'false') === 'true';
    this.priceScale = this.cps.getPropertyValue('erp.use-three-digit-prices', 'false') === 'true' ? 3 : 2;

    this.allowBasicPriceSelectionOption = this.basic === false && this.mode !== 'costs';

    this.resultingPriceCalculationDate = new Date();
    this.resultingPriceCalculationDate.setHours(0);
    this.resultingPriceCalculationDate.setMilliseconds(0);
    this.resultingPriceCalculationDate.setMinutes(0);
    this.resultingPriceCalculationDate.setSeconds(0);

    this.formGroup.addControl('resultingprice_calculation_date', new FormControl(this.resultingPriceCalculationDate, []));

    this.formGroup.addControl('date_from', new FormControl(this.price.validPeriod.from, [Validators.required]));
    this.formGroup.addControl('date_until', new FormControl(this.price.validPeriod.until, []));
    this.formGroup.addControl('source', new FormControl(null, [Validators.required]));
    this.formGroup.addControl('reference', new FormControl(this.price.referenceDate, []));
    this.formGroup.addControl('price_net', new FormControl(this.price.priceNet, []));
    this.formGroup.addControl('tax_rate', new FormControl(this.price.taxRate, []));
    this.formGroup.addControl('price_vat', new FormControl(this.price.priceVat, []));

    if (this.price instanceof RetailPrice) {
      this.formGroup.controls.source.disable();
      const retailPrice = this.price as RetailPrice;
      for (const rule of this.articleType.calculationRules) {
        const value = retailPrice.getValueByRuleId(rule.id);
        const ruleCountFormControl = new FormControl(value != null ? value.count : null, []);
        this.formGroup.addControl('rule_count_' + rule.id, ruleCountFormControl);
        this.formGroup.addControl('rule_faktor_' + rule.id, new FormControl(value != null ? value.value : null, []));
        if (rule.precission === ArticleTypeCalculationPrecission.ATTRIBUTE) {
          const attributeValue = this.article.getValueByAttributeId(rule.attributeId);
          const countValue = parseFloat(attributeValue);
          ruleCountFormControl.setValue(isNaN(countValue) ? null : countValue);
          ruleCountFormControl.disable();
        }
      }
      this.formGroup.addControl('useBasicPrice', new FormControl(retailPrice.useBasicPrice + '', []));
      this.useBasicPriceOptionSelected = retailPrice.useBasicPrice;
      this.setPriceFieldsEnablement(retailPrice);
    } else {
      this.formGroup.controls.source.enable();
    }
  }

  ngOnInit(): void {

    this.loadBasicArticle();

    this.loadAvailableCostsPriceSources();

    this.costsPriceAttributeGroups = this.articleType.attributeGroups.filter(g => g.usage === AttributeGroupUsage.COSTSPRICE);

    this.formGroup.valueChanges.subscribe(formValues => {
      console.log('formGroup Values Changed', formValues);

      if (this.processingFormChange) {
        return;
      }

      this.processingFormChange = true;

      if (formValues.reference != null) {
        this.price.referenceDate = DateTime.fromJSDate(formValues.reference).toFormat(CUSTOM_DATE_FORMAT.display.dateInput);
      }
      this.price.validPeriod.from = formValues.date_from;
      this.price.validPeriod.until = formValues.date_until;

      if (this.price instanceof CostsPrice) {
        let costPrice = this.price as CostsPrice;
        costPrice.source = formValues.source;
      }

      this.price.taxRate = formValues.tax_rate != null && formValues.tax_rate !== '' ? parseFloat(formValues.tax_rate) : null;
      this.price.priceNet = formValues.price_net != null && formValues.price_net !== '' ? parseFloat(formValues.price_net) : null;
      this.price.priceVat = formValues.price_vat != null && formValues.price_vat !== '' ? parseFloat(formValues.price_vat) : null;

      if (this.price instanceof RetailPrice) {
        const retailPrice = this.price as RetailPrice;

        for (const calculationRule of this.articleType.calculationRules) {
          const value = this.getOrCreateValue(retailPrice, calculationRule.id);
          let parsedCount = 0;
          if (calculationRule.precission === ArticleTypeCalculationPrecission.ATTRIBUTE) {
            parsedCount = parseFloat(this.article.getValueByAttributeId(calculationRule.attributeId));
          } else {
            parsedCount = parseFloat(formValues['rule_count_' + calculationRule.id]);
          }
          const parsedValue = parseFloat(formValues['rule_faktor_' + calculationRule.id]);
          value.count = isNaN(parsedCount) ? null : parsedCount;
          value.value = isNaN(parsedValue) ? null : parsedValue;
          value.calculate();
          this.calculationResults.set(calculationRule.id, value.total);
        }

        retailPrice.useBasicPrice = formValues.useBasicPrice == 'true';
        // wechsel zu Preisangabe enabled nur die Fields. In den FormValues sind die Werte nciht drin. daher in  diesem Fall Auslesen aus
        // den FormFields direkt
        if (this.useBasicPriceOptionSelected !== retailPrice.useBasicPrice && retailPrice.useBasicPrice === false) {
          this.price.taxRate = this.formGroup.controls.tax_rate.value != null && this.formGroup.controls.tax_rate.value !== '' ? parseFloat(this.formGroup.controls.tax_rate.value) : null;
          this.price.priceNet = this.formGroup.controls.price_net.value != null && this.formGroup.controls.price_net.value !== '' ? parseFloat(this.formGroup.controls.price_net.value) : null;
          this.price.priceVat = this.formGroup.controls.price_vat.value != null && this.formGroup.controls.price_vat.value !== '' ? parseFloat(this.formGroup.controls.price_vat.value) : null;
        }

        this.useBasicPriceOptionSelected = retailPrice.useBasicPrice;
        this.setPriceFieldsEnablement(retailPrice);

      }

      this.resultingPriceCalculationDate = formValues.resultingprice_calculation_date;
      // calculate resulting price
      this.calculateResultingPrice();

      this.processingFormChange = false;

    });

    this.formGroup.statusChanges.subscribe(newStatus => {
      this.confirmDisabled = !this.formGroup.valid;
      console.log('-------- pricestatuschange', this.confirmDisabled, this.formGroup);
    });

    const formValuesToUpdate = {
      source: this.price instanceof CostsPrice ? (this.price as CostsPrice).source : '',
      reference: this.price.referenceDate != null
            ? this.getJSDate(this.price.referenceDate)
            : new Date(),
      tax_rate: this.price.taxRate != null ? this.price.taxRate : 19
    };

    this.formGroup.patchValue(formValuesToUpdate);

    this.confirmDisabled = !this.formGroup.valid;
    this.formGroup.markAllAsTouched();
  }

  private setPriceFieldsEnablement(price: RetailPrice) {
    let fields = ['price_net', 'tax_rate', 'price_vat'];
    for (let fieldId of fields) {
      if (price.useBasicPrice && this.mode == 'retail' && !this.basic) {
        this.formGroup.controls[fieldId].disable();
      } else {
        this.formGroup.controls[fieldId].enable();
      }
    }
  }

  private getOrCreateValue(retailPrice: RetailPrice, id: number): RetailPriceCalculationValue {
    for (let value of retailPrice.calculationValues) {
      if (value.ruleAttributeId == id) {
        return value;
      }
    }
    let value = new RetailPriceCalculationValue({ id: this.idGenerator.getNextId() });
    value.ruleAttributeId = id;
    retailPrice.calculationValues.push(value);
    return value;
  }

  onClose() {
    this.dialogRef.close();
  }

  onConfirm() {
    console.log('price object is on close', this.price);
    this.dialogRef.close(this.price);
  }

  public onDashboardTitleChanged(dictionary: DictionaryEntry) {

  }

  private getJSDate(d: string) {
    let parsedDate = null;
    if (d != null && d.length > 0) {
      const p = DateTime.fromFormat(d, CUSTOM_DATE_FORMAT.parse.dateInput);
      if (p.isValid) {
        parsedDate = new Date(p.year, p.month - 1, p.day);
      }
    }
    return parsedDate;
  }

  private loadBasicArticle() {
    this.appServerAPI.execute<BasicArticle>('/client/erp-article/article/loadBasicArticle', {articleId: this.article.id}).subscribe(basicArticle => {
      if (basicArticle != null) {
        this.basicArticle = new BasicArticle(basicArticle);
        this.calculateResultingPrice();
      }
    }, error => {
      console.log('error is', error);
    });

  }

  private calculateResultingPrice() {
    let totalPrice = null;
    if (this.price instanceof RetailPrice && (this.price as RetailPrice).useBasicPrice === true) {
      if (this.basicArticle != null) {
        for (const currentRetailPrice of this.basicArticle.priceContainer.retailPrices) {
          if (currentRetailPrice.validPeriod.from.getTime() <= this.resultingPriceCalculationDate.getTime()
              && currentRetailPrice.validPeriod.until.getTime() >= this.resultingPriceCalculationDate.getTime()) {
            totalPrice = currentRetailPrice.priceNet;
            break;
          }
        }
      }
    } else {
      totalPrice = this.price.priceNet;
    }

    if (totalPrice != null && this.price instanceof RetailPrice) {
      const p = this.price as RetailPrice;
      this.calculationResults.forEach(cV => {
        if (cV != null) {
          totalPrice = totalPrice + cV;
        }
      });
    }
    this.resultingPriceNet = totalPrice;
  }

  private loadAvailableCostsPriceSources() {
    this.appServerAPI.execute<CostsPriceSources>('/client/erp-article/article/getCostsPriceSources', {}).subscribe(costsPriceSources => {
      const costPriceSources = new CostsPriceSources(costsPriceSources);
      costPriceSources.sources.forEach(s => this.costsPriceSources.push(s));
    }, error => {
      console.log('error is', error);
    });
  }

}

